// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { SelectItem, Slider } from '@advisor-app/components';
import {
  selectHistoricalWindow,
  selectOptimizationConfigValues,
  setHistoricalWindow,
  updateOptimizationConfig,
} from '@advisor-app/store/slices/parameters';
import { selectWorkflowState } from '@advisor-app/store/slices/workflow';
import { useAppSelector } from '@advisor-app/store/store';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { Heading, SectionContainer } from '@shared/ui-kit/components';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

interface OptimizationSelectorProps {
  maxNumberStocks: number;
}

export const OptimizationSelector = ({
  maxNumberStocks,
}: OptimizationSelectorProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const historicalWindow = useAppSelector(selectHistoricalWindow);
  const optimizationConfig = useAppSelector(state =>
    selectOptimizationConfigValues(state, 'ai-builder'),
  );
  const { objective, max_weight, min_weight, number_assets } = optimizationConfig;
  const weight = [min_weight, max_weight];

  const isDirectIndexingSelected = objective.key === 'min_tracking_error';

  const allowPositionsWeightsSlider =
    objective.key === 'min_risk_parity' || objective.key === 'equal_weights';

  const workflow = useAppSelector(selectWorkflowState);

  const valueLabelFormat = useCallback(
    (value: number) => Number((value * 100).toFixed(2)) + `%`,
    [],
  );

  const historicalWindows = [
    { key: t('ai-builder.6-months', { ns: 'ai-builder' }) as string, value: 182 },
    { key: t('ai-builder.1-year', { ns: 'ai-builder' }) as string, value: 365 },
    { key: t('ai-builder.3-years', { ns: 'ai-builder' }) as string, value: 1095 },
  ];

  const updateNumberAssets = useCallback(
    (_: unknown, value: number | number[]) => {
      const number_assets = value as [number, number];
      dispatch(updateOptimizationConfig(['ai-builder', { number_assets }]));
    },
    [dispatch],
  );

  const updateWeight = useCallback(
    (_: unknown, value: number | number[]) => {
      const [min_weight, max_weight] = value as [number, number];
      dispatch(
        updateOptimizationConfig(['ai-builder', { min_weight, max_weight }]),
      );
    },
    [dispatch],
  );

  const shouldDisplayPositionsWeightsSlider = ['stock'].includes(workflow.asset);

  return (
    <SectionContainer>
      <Accordion
        key="optimization_parameters"
        className="!-mx-3.5 !-mt-4 !bg-section"
      >
        <AccordionSummary className="-my-4 flex-1">
          <Heading
            title={
              t('ai-builder.select-optimization-parameters', {
                ns: 'ai-builder',
              }) as string
            }
            popupName="ai-builder.selectOptimizationParameters"
            tooltip
            variant="h3"
          />
        </AccordionSummary>
        <AccordionDetails>
          <div className="grid grid-cols-2 gap-16">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Heading
                  title={
                    t('ai-builder.number-of-positions', {
                      ns: 'ai-builder',
                    }) as string
                  }
                  popupName="ai-builder.numberOfPositions"
                  tooltip
                  variant="h5"
                  className="!mb-5"
                />
                <Slider
                  disabled={!isDirectIndexingSelected}
                  value={number_assets}
                  onChangeCommitted={updateNumberAssets}
                  onChange={updateNumberAssets}
                  valueLabelDisplay="on"
                  min={1}
                  max={maxNumberStocks}
                  data-testid="stocks-slider"
                  classes={{ valueLabel: '!bg-section' }}
                  className="mx-2 w-auto"
                />
              </div>

              {shouldDisplayPositionsWeightsSlider && (
                <div className="grid gap-2">
                  <Heading
                    title={
                      t('ai-builder.positions-weights', {
                        ns: 'ai-builder',
                      }) as string
                    }
                    popupName="ai-builder.positionsWeights"
                    tooltip
                    variant="h5"
                    className="!mb-5"
                  />
                  <Slider
                    disabled={allowPositionsWeightsSlider}
                    value={weight}
                    onChangeCommitted={updateWeight}
                    onChange={updateWeight}
                    valueLabelDisplay="on"
                    valueLabelFormat={valueLabelFormat}
                    step={0.0001}
                    min={0}
                    max={1}
                    data-testid="weight-slider"
                    className="mx-2 w-auto "
                    classes={{ valueLabel: '!bg-section' }}
                  />
                </div>
              )}
            </div>
            <div className="">
              <Heading
                title={
                  t('ai-builder.optimization-historical-window', {
                    ns: 'ai-builder',
                  }) as string
                }
                popupName="ai-builder.optimizationHistoricalWindow"
                tooltip
                variant="h5"
                className="mb-3"
              />
              <div className="boxwrapper grid_2">
                {historicalWindows.map(({ key, value }) => (
                  <SelectItem
                    key={key}
                    label={key}
                    labelId={String(value)}
                    checkedValidator={String(historicalWindow)}
                    groupName="histw"
                    handleSelectItem={() => dispatch(setHistoricalWindow(value))}
                  />
                ))}
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </SectionContainer>
  );
};
